import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { evidenceKeys, type EvidenceDocument } from "@lumanity/ui/evidence";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import type { Document } from "@/types/models";

/**
 * Glue between @lumanity/ui's EvidenceBase and the rest of FRaiME, which
 * still reads documents through its own hooks and opens them in its own
 * viewer (chat citations use it too).
 */

/** FRaiME's viewer takes its own Document shape; it reads id, filename, title, author, year and sourceType. */
export function toViewerDocument(doc: EvidenceDocument): Document {
  return {
    id: doc.id,
    filename: doc.filename,
    title: doc.title,
    author: doc.authors,
    description: doc.description,
    tags: doc.tags,
    sourceType: doc.sourceType,
    sourceOrganisation: doc.organization,
    country: doc.country,
    year: doc.year,
    evidenceType: doc.evidenceType,
    dateRelevance: doc.dataRetention ?? "",
    url: doc.url,
    language: doc.language,
    ownership: doc.ownership,
    createdAt: doc.createdAt,
  };
}

/** The eye button: open the document in FRaiME's viewer sheet. */
export function useOpenEvidenceDocument() {
  const { openDocument } = useDocumentViewer();
  return useCallback((doc: EvidenceDocument) => openDocument({ doc: toViewerDocument(doc) }), [openDocument]);
}

/**
 * Both caches hold documents: the library's (`["evidence", …]`, behind the
 * grid) and FRaiME's (`["documents"]`, `["projects", id, "documents"]`, behind
 * the pages' "has documents" logic and the upload duplicate check). After any
 * change, refresh both so neither shows stale rows.
 */
export function useRefreshDocuments() {
  const qc = useQueryClient();
  return useCallback(
    (projectId?: string) => {
      void qc.invalidateQueries({ queryKey: evidenceKeys.all });
      void qc.invalidateQueries({ queryKey: ["documents"] });
      if (projectId) void qc.invalidateQueries({ queryKey: ["projects", projectId, "documents"] });
    },
    [qc],
  );
}
